(function(window, volumeControlViewFactory) {
   'use strict';

   var namespace = window.TSC = window.TSC || {};
   namespace.VolumeControlView = volumeControlViewFactory(
      window.$,
      namespace.localStorageWrapper,
      namespace.localizationStrings,
      namespace.templates,
      namespace.deviceInfo,
      namespace.cssClasses,
      namespace.tabIndex,
      namespace.events,
      namespace.highlightDisabler);
})(this, function($, localStorageWrapper, localizationStrings, templates, deviceInfo, cssClasses, tabIndex, events, highlightDisabler) {
   'use strict';
   var VOLUME_CACHE_KEY = 'volume';
   var MUTED_CACHE_KEY = 'muted';
   var VOLUME_SLIDER_MIN = 0;
   var VOLUME_SLIDER_MAX = 100;
   var MOUSEOUT_HIDE_SLIDER_TIMEOUT = 1000;
   var FOCUSOUT_HIDE_SLIDER_TIMEOUT = 200;
   var VOLUME_BUTTON_STRING = localizationStrings.strings.accessBtnVolume;
   var VOLUME_STRING = localizationStrings.strings.accessTxtVolume;

   /**
    * @memberof TSC
    * @class VolumeControlView
    * @classdesc VolumeControl View
    */
   var VolumeControlView = function($container, mediaElement) {
      var _cachedVolume = localStorageWrapper.getItem(VOLUME_CACHE_KEY);
      var _cachedMuted = localStorageWrapper.getItem(MUTED_CACHE_KEY);
      var _sliderShowTimeoutId = -1;
      var _markup = templates.volume_control_template({
         accessBtnVolume: VOLUME_BUTTON_STRING,
         volumeIndex: tabIndex.Volume
      });

      var _$volumeContainer = $container.find('.volume-wrapper');
      _$volumeContainer.html(_markup);
      var _$volumeSlider = _$volumeContainer.find('.volume-slider');
      var _$volumeSliderContainer = _$volumeContainer.find('.volume-slider-container');
      var _$volumeControl = _$volumeContainer.find('.volume');
      var _$volumeSliderHandle;
      var _initialVolume = _cachedVolume === undefined || _cachedVolume === null ? 1 : +_cachedVolume;
      var _initialMuted = _cachedMuted === 'true';

      if (deviceInfo.getIOSSupportsInlineVideo() || deviceInfo.isAndroid()) {
         _$volumeControl.remove();
      }

      var setupVolumeSlider = function(volumeSliderObject) {
         volumeSliderObject.slider({
            orientation: 'horizontal',
            range: 'min',
            min: VOLUME_SLIDER_MIN,
            max: VOLUME_SLIDER_MAX,
            step: 2,
            value: mediaElement.volume * VOLUME_SLIDER_MAX,
            slide: function(event, ui) {
               setMuted(false);
               setVolume(ui.value / VOLUME_SLIDER_MAX);
            },
            stop: onSliderDragEnd
         });
      };

      var showVolumeSlider = function() {
         _$volumeSliderContainer.removeClass(cssClasses.hide);
      };

      var hideControls = function() {
         if (_sliderShowTimeoutId !== -1) {
            hideVolumeSlider();
         }
      };

      var hideVolumeSlider = function() {
         if (_sliderShowTimeoutId !== -1) {
            clearTimeout(_sliderShowTimeoutId);
            _sliderShowTimeoutId = -1;
         }

         _$volumeSliderContainer.addClass(cssClasses.hide);
      };

      var updateUi = function() {
         var volPercent = mediaElement.muted ? 0 : Math.round(mediaElement.volume * VOLUME_SLIDER_MAX);
         _$volumeSliderHandle.attr('aria-valuenow', volPercent);
         _$volumeSliderHandle.attr('aria-valuetext', volPercent + '% ' + VOLUME_STRING);
         _$volumeControl.attr('aria-pressed', !!mediaElement.muted);

         _$volumeSlider.slider('value', [volPercent]);
         _$volumeControl.removeClass(cssClasses.volumeHighButtonGlyph);
         _$volumeControl.removeClass(cssClasses.volumeMediumButtonGlyph);
         _$volumeControl.removeClass(cssClasses.volumeLowButtonGlyph);
         _$volumeControl.removeClass(cssClasses.volumeMutedButtonGlyph);

         if (volPercent === 0) {
            _$volumeControl.addClass(cssClasses.volumeMutedButtonGlyph);
         } else if (mediaElement.volume > .7) {
            _$volumeControl.addClass(cssClasses.volumeHighButtonGlyph);
         } else if (mediaElement.volume > .3) {
            _$volumeControl.addClass(cssClasses.volumeMediumButtonGlyph);
         } else {
            _$volumeControl.addClass(cssClasses.volumeLowButtonGlyph);
         }
      };

      var setVolume = function(newVol) {
         if (newVol > 1) {
            newVol = 1.0;
         } else if (newVol < 0) {
            newVol = 0.0;
         }

         localStorageWrapper.setItem(VOLUME_CACHE_KEY, newVol);
         mediaElement.volume = newVol;
      };

      var setMuted = function(muted) {
         mediaElement.muted = muted;
         localStorageWrapper.setItem(MUTED_CACHE_KEY, muted);
      };

      var onVolumeChanged = function() {
         updateUi();
      };

      var startHideSliderTimeout = function(timeout) {
         if (_sliderShowTimeoutId !== -1) {
            clearTimeout(_sliderShowTimeoutId);
         }

         _sliderShowTimeoutId = setTimeout(function() {
            hideVolumeSlider();
            _sliderShowTimeoutId = -1;
         }, timeout);
      };

      var isKeyboardEvent = function(e) {
         return e.key || e.originalEvent && e.originalEvent.keyCode;
      };

      var onSliderDragEnd = function(e) {
         if (!_$volumeContainer.has(e.toElement).length && !isKeyboardEvent(e)) {
            startHideSliderTimeout(MOUSEOUT_HIDE_SLIDER_TIMEOUT);
         }
      };

      var onContainerMouseOver = function() {
         if (_sliderShowTimeoutId !== -1) {
            clearTimeout(_sliderShowTimeoutId);
            _sliderShowTimeoutId = -1;
         }

         showVolumeSlider();
      };

      var onContainerMouseLeave = function(e) {
         if (e.buttons !== 1) {
            startHideSliderTimeout(MOUSEOUT_HIDE_SLIDER_TIMEOUT);
         }
      };

      var hideVolumeSliderOnFocusOut = function() {
         startHideSliderTimeout(FOCUSOUT_HIDE_SLIDER_TIMEOUT);
      };

      var onVolumeButtonClick = function() {
         if (!mediaElement.muted && mediaElement.volume === 0) {
            setVolume(1);
         } else {
            setMuted(!mediaElement.muted);
         }
      };

      var disableControls = function() {
         _$volumeControl.attr('tabindex', tabIndex.Disabled);
         _$volumeSliderHandle.attr('tabindex', tabIndex.Disabled);
      };

      var enableControls = function() {
         _$volumeControl.attr('tabindex', tabIndex.Volume);
         _$volumeSliderHandle.attr('tabindex', tabIndex.VolumeSlider);
      };

      var onSliderMouseDownHandler = function() {
         highlightDisabler.disableHighlights(_$volumeSliderHandle);
      };

      var initializeHandlers = function() {
         setupVolumeSlider(_$volumeSlider);

         _$volumeSliderHandle = _$volumeContainer.find('.ui-slider-handle');

         _$volumeContainer.on('mousemove', onContainerMouseOver);
         _$volumeContainer.on('mouseout', onContainerMouseLeave);
         _$volumeControl.on('click', onVolumeButtonClick);
         _$volumeSlider.on('mousedown', onSliderMouseDownHandler);
         _$volumeSliderHandle.on('focus', showVolumeSlider);
         _$volumeSliderHandle.on('focusout', hideVolumeSliderOnFocusOut);
         _$volumeSliderHandle.attr('tabindex', tabIndex.VolumeSlider);
         _$volumeSliderHandle.attr('aria-valuemin', VOLUME_SLIDER_MIN);
         _$volumeSliderHandle.attr('aria-valuemax', VOLUME_SLIDER_MAX);
         _$volumeSliderHandle.attr('role', 'slider');

         $container.on(events.Controls.Disable, disableControls);
         $container.on(events.Controls.Enable, enableControls);
      };

      var destroy = function() {
         _$volumeContainer.off('mousemove', onContainerMouseOver);
         _$volumeContainer.off('mouseout', onContainerMouseLeave);
         _$volumeControl.off('click', onVolumeButtonClick);
         _$volumeSlider.off('mousedown', onSliderMouseDownHandler);
         _$volumeSliderHandle.off('focus', showVolumeSlider);
         _$volumeSliderHandle.off('focusout', hideVolumeSliderOnFocusOut);
         $container.off(events.Controls.Disable, disableControls);
         $container.off(events.Controls.Enable, enableControls);
      };

      initializeHandlers();
      setMuted(_initialMuted);
      setVolume(_initialVolume);
      updateUi();

      return Object.defineProperties({
         hideControls: hideControls,
         onVolumeChanged: onVolumeChanged,
         destroy: destroy
      }, {
         volume: {
            get: function() {
               return mediaElement.volume;
            },
            set: setVolume
         },
         muted: {
            get: function() {
               return mediaElement.muted;
            },
            set: setMuted
         }
      });
   };

   return {
      /**
       *  Factory method that returns a new VolumeControlView object.
       *  @function create
       *  @memberof TSC.VolumeControlView
       *  @static
       *  @param {Object} $container - jQuery element that contains a .volume-wrapper element where the volume controls will be injected
       *  @param {Object} mediaElement - html5 media DOM element (or wrapper presenting same interface)
       *  @return new VolumeControlView instance
       */
      create: VolumeControlView
   };
});
